import {
  SmartReviewTransaction,
  SmartReviewVendor,
  useUpdateMultipleTransactionsDataMutation,
} from "store/apis/transactions";
import { createColumnHelper } from "react-table-8.10.7";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { SmartReviewTxnTable } from "./SmartReviewTxnTable";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { CategorisationHistoryModal } from "components/CategorisationHistory/CategorisationHistoryModal";
import { useModal } from "hooks/useModal";
import ToolTip from "components/design/toolTip";
import { ClockCounterClockwise } from "components/icons/ClockCounterClockwise";
import { colors } from "constants/colors";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedUi } from "components/LockedUi/LockedUi";

type VendorTableMeta = {
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabled: boolean;
};

const columnHelper = createColumnHelper<SmartReviewTransaction>();

const selectColumn = columnHelper.display({
  id: "select",
  size: 10,
  header: ({ table }) => {
    const { selectedIds, onToggle, disabled } = table.options
      .meta as VendorTableMeta;
    const allTxnIds = table.getCoreRowModel().rows.map((r) => r.original.id);
    const allSelected =
      allTxnIds.length > 0 && allTxnIds.every((id) => selectedIds.includes(id));
    const someSelected = allTxnIds.some((id) => selectedIds.includes(id));
    return (
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && someSelected}
        disabled={disabled}
        onChange={() => {
          if (allSelected) {
            allTxnIds.forEach((id) => onToggle(id));
          } else {
            allTxnIds
              .filter((id) => !selectedIds.includes(id))
              .forEach((id) => onToggle(id));
          }
        }}
      />
    );
  },
  cell: ({ row, table }) => {
    const { selectedIds, onToggle, disabled } = table.options
      .meta as VendorTableMeta;
    return (
      <Checkbox
        checked={selectedIds.includes(row.original.id)}
        disabled={disabled}
        onChange={() => onToggle(row.original.id)}
        onClick={(e) => e.stopPropagation()}
      />
    );
  },
});

type SmartReviewVendorGroupProps = {
  vendor: SmartReviewVendor;
  selectedIds: string[];
  confirmingIds: string[];
  updatingCategoryIds: string[];
  onToggleTransaction: (vendorId: string, txnId: string) => void;
  onToggleVendor: (vendorId: string) => void;
  onCategoryChange?: (txnId: string, categoryId: string | null) => void;
};

export const SmartReviewVendorGroup = ({
  vendor,
  selectedIds,
  confirmingIds,
  updatingCategoryIds,
  onToggleTransaction,
  onToggleVendor,
  onCategoryChange,
}: SmartReviewVendorGroupProps) => {
  const categorisationHistoryModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { successToast, alertToast } = useToast();

  const [updateMultipleTransactions, { isLoading: isAssigning }] =
    useUpdateMultipleTransactionsDataMutation();

  const allSelected = vendor.transactions.every((t) =>
    selectedIds.includes(t.id)
  );
  const someSelected = vendor.transactions.some((t) =>
    selectedIds.includes(t.id)
  );
  const confirmingCount = confirmingIds.length;
  const isConfirming = confirmingCount > 0;

  const vendorKey = vendor.merchant_data_id ?? "unknown_merchant";

  const handleVendorCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isConfirming) onToggleVendor(vendorKey);
  };

  const handleCategoryAssign = async (categoryId: string) => {
    try {
      await updateMultipleTransactions({
        groupId,
        entityId,
        transactions: vendor.transactions.map((t) => ({
          uuid: t.id,
          category_id: categoryId,
        })),
      }).unwrap();
      successToast({ message: "Category assigned successfully" });
      categorisationHistoryModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <div className="t-flex t-items-start t-gap-3 t-mb-3">
      <LockedUi feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}>
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          disabled={isConfirming}
          onChange={() => {}}
          onClick={handleVendorCheckbox}
          className="t-mt-[18px]"
        />
      </LockedUi>
      <Accordion.Root type="single" collapsible className="t-min-w-0 t-flex-1">
        <Accordion.Item value={vendorKey}>
          <Accordion.Trigger className="t-group">
            <div className="t-flex t-justify-between t-w-full">
              <div className="t-flex t-flex-col t-items-start">
                <div className="t-flex t-items-center t-gap-1">
                  <span className="t-text-subtext t-text-text-60">
                    {vendor.vendor_name ?? "Unknown vendor"}
                  </span>
                  {vendor.merchant_data_id && (
                    <span className="t-opacity-0 group-hover:t-opacity-100 t-transition-opacity">
                      <ToolTip text="Categorisation history">
                        <Button
                          customType="ghost_icon"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            categorisationHistoryModal.open();
                          }}
                        >
                          <ClockCounterClockwise
                            color={colors.text[30]}
                            size={16}
                          />
                        </Button>
                      </ToolTip>
                    </span>
                  )}
                </div>
                <span className="t-text-body-sm t-text-text-30">
                  Transactions: {vendor.transaction_count} | Category:{" "}
                  {vendor.category_name ?? "Uncategorised"}
                </span>
              </div>
              {confirmingCount > 0 && (
                <div className="t-flex t-gap-2 t-items-center">
                  <Loader size="small" />
                  <span className="t-text-button-sm t-text-text-60">
                    confirming {confirmingCount}/{vendor.transaction_count}...
                  </span>
                </div>
              )}
            </div>
          </Accordion.Trigger>
          <Accordion.Content>
            <SmartReviewTxnTable
              transactions={vendor.transactions}
              selectColumn={selectColumn}
              confirmingIds={confirmingIds}
              isSelected={(txn) => selectedIds.includes(txn.id)}
              isRowClickable={() => true}
              onRowClick={(txn) => onToggleTransaction(vendorKey, txn.id)}
              meta={{
                selectedIds,
                onToggle: (id: string) => onToggleTransaction(vendorKey, id),
                disabled: isConfirming,
                onCategoryChange,
                updatingCategoryIds,
              }}
            />
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>

      {categorisationHistoryModal.isOpen && vendor.merchant_data_id && (
        <CategorisationHistoryModal
          isOpen={categorisationHistoryModal.isOpen}
          onClose={categorisationHistoryModal.close}
          merchantId={vendor.merchant_data_id}
          vendorName={vendor.vendor_name ?? ""}
          onCategoryAssign={handleCategoryAssign}
          isAssigning={isAssigning}
          readOnly={!hasWritePermission}
        />
      )}
    </div>
  );
};
